import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';

import { AccountPickerComponent } from '@app/components/account-picker/account-picker.component';
import { ClientPickerComponent } from '@app/components/client-picker/client-picker.component';
import { ReportPickerComponent } from '@app/components/report-picker/report-picker.component';
import { AuthService } from '@auth/services/auth.service';
import { ClientAccess } from '@core-interactions/models/client-access';
import { Module } from '@core-interactions/models/module';
import { ACCESS_STORE } from '@core-interactions/stores/access.store';
import { ENVIRONMENT } from '@env/environment';
import { ClientPowerBIReport } from '@power-bi/models/client-power-bi-report';
import { AccountSignatureComponent } from '@shared/components/account-signature/account-signature.component';
import { FindByPropertyPipe } from '@shared/pipes/find-by-property.pipe';
import { ModuleClientService } from '@shared/services/module-client.service';
import { RouterService } from '@shared/services/router.service';
import { APP_STORE } from '@stores/app.store';
import { Account } from '@stores/models/app/account';
import { AccountType } from '@stores/models/app/account-type';
import { USER_STORE } from '@stores/user.store';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatToolbarModule,
    RouterLink,
    ReportPickerComponent,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    AccountSignatureComponent,
    AccountPickerComponent,
    ClientPickerComponent,
    FindByPropertyPipe
  ]
})
export class NavbarComponent {
  readonly #router = inject(Router);
  readonly #routerService = inject(RouterService);
  readonly #authService = inject(AuthService);
  readonly #moduleClientService = inject(ModuleClientService);
  readonly #appStore = inject(APP_STORE);
  readonly #userStore = inject(USER_STORE);
  readonly #accessStore = inject(ACCESS_STORE);

  readonly user = this.#userStore.data;

  readonly selectedAccount = this.#appStore.selectedAccount;

  readonly selectedReportId = this.#appStore.selectedReportId;
  readonly selectedClientId = this.#appStore.selectedClientId;
  readonly selectedAccountId = this.#appStore.selectedAccountId;

  readonly clientSetupLoading = this.#appStore.clientSetupLoading;

  readonly reports = this.#accessStore.reports;
  readonly clients = this.#accessStore.clients;
  readonly modules = this.#accessStore.modules;

  readonly isSystemAdmin = computed(
    () =>
      this.#appStore.selectedAccount()?.accountType === AccountType.SystemAdmin
  );

  readonly pickersDisabled = computed(
    () =>
      this.#appStore.clientSetupLoading() || this.#accessStore.reports().loading
  );

  readonly displayAccountPicker = computed(() => {
    const user = this.#userStore.data();
    return (
      this.#appStore.selectedAccount()?.accountType !==
        AccountType.SystemAdmin &&
      !!this.#appStore.selectedAccountId() &&
      !!user?.accounts?.length
    );
  });

  readonly displayClientPicker = computed(
    () =>
      this.#appStore.selectedAccount()?.accountType ===
        AccountType.SystemAdmin &&
      !!this.#appStore.selectedClientId() &&
      this.#accessStore.clients().loaded &&
      this.#accessStore.clients().entities.length > 0
  );

  readonly displayReportPicker = computed(
    () =>
      !!this.#appStore.selectedReportId() &&
      this.#accessStore.reports().loaded &&
      this.#accessStore.reports().entities.length > 0
  );

  modulesWithAccess: Module[] = [];
  modulesWithoutAccess: Module[] = [];

  readonly CoreModule = ENVIRONMENT.coreModule;

  constructor() {
    effect(() => {
      if (!this.#accessStore.modules().loaded) {
        return;
      }

      this.populateModulesArrays();
    });
  }

  selectAccount(account: Account): void {
    this.#appStore.selectAccount(account.id, account.client?.id ?? '');

    void this.#router.navigate(['/client-setup'], {
      queryParams: {
        clientId: account.client?.id,
        redirectTo: this.#routerService.currentParentRoute
      }
    });
  }

  selectClient(selectedClient: ClientAccess): void {
    this.#appStore.selectClient(selectedClient.id);

    void this.#router.navigate(['/client-setup'], {
      queryParams: {
        clientId: selectedClient.id,
        redirectTo: this.#routerService.currentParentRoute
      }
    });
  }

  selectReport(selectedReport: ClientPowerBIReport): void {
    this.#appStore.selectReport(selectedReport.id);
    void this.#router.navigateByUrl('/');
  }

  populateModulesArrays(): void {
    this.modulesWithAccess = [];
    this.modulesWithoutAccess = [];

    const account = this.#appStore.selectedAccount();
    const modules = this.#accessStore.modules().entities;

    for (const module of modules) {
      if (this.#moduleClientService.hasAccess(module, account)) {
        this.modulesWithAccess.push(module);
      } else {
        this.modulesWithoutAccess.push(module);
      }
    }
  }

  onUserProfileMenuTriggered(): void {
    setTimeout(() => {
      const selectedClient = document.getElementsByClassName(
        'selectedClient'
      )[0] as HTMLElement | undefined;

      const selectedAccount = document.getElementsByClassName(
        'selectedAccount'
      )[0] as HTMLElement | undefined;

      (selectedClient ?? selectedAccount)?.scrollIntoView({ block: 'start' });
    });
  }

  navigateToModule(module: Module): void {
    const clientId = this.#appStore.selectedClientId();
    const account = this.#appStore.selectedAccount();

    const query = clientId ? `?clientId=${clientId}` : '';
    let url = `${module.locationUrl}${query}`;

    if (
      module.name !== ENVIRONMENT.coreModule.name &&
      !this.#moduleClientService.hasAccess(module, account)
    ) {
      url = module.landingPageUrl ?? ENVIRONMENT.appRoot;
    }

    if (url !== ENVIRONMENT.appRoot) {
      window.open(url);
    } else {
      window.location.href = url;
    }
  }

  navigateToMyAccount(account: Account): void {
    let location = `${this.CoreModule.locationUrl}my-profile`;

    if (account.accountType !== AccountType.SystemAdmin) {
      location += `?clientId=${account.client?.id ?? ''}`;
    }

    window.location.href = location;
  }

  signOut(): void {
    void this.#authService.signOut();
    localStorage.clear();
  }
}
